import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";

// Somme des enregistrements de toutes les collections d'une sauvegarde.
const totalRecords = (b) => Object.values(b.db_counts || {}).reduce((a, c) => a + c, 0);

const DEST_TYPES = [
  { type: "s3", icon: "Cloud", label: "Amazon S3 / Glacier" },
  { type: "azure", icon: "Cloud", label: "Azure Blob" },
  { type: "sftp", icon: "Server", label: "Synology / SFTP" },
];

export default function Backup() {
  const { t, lang } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [backups, setBackups] = useState([]);
  const [running, setRunning] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [destModal, setDestModal] = useState(null);   // {type} (nouveau) ou destination (édition)
  const [restoreFile, setRestoreFile] = useState(false);
  const [wipeModal, setWipeModal] = useState(false);

  const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const fmtSize = (b) => { if (!b) return "0 o"; const u = ["o", "Ko", "Mo", "Go"]; const i = Math.floor(Math.log(b) / Math.log(1024)); return `${(b / Math.pow(1024, i)).toFixed(1)} ${u[i]}`; };

  const load = useCallback(async () => {
    try {
      const [c, b] = await Promise.all([api.get("/backup/config"), api.get("/backup")]);
      setCfg(c.data); setBackups(b.data);
    } catch { toast.error(t("load_failed")); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const runBackup = async () => {
    if (!cfg?.encryption_set) { setPwModal(true); toast.error(t("bkp_need_password")); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/backup/run", {}, { timeout: 300000 });
      const errs = (data.destinations || []).filter((d) => d.status === "error");
      if (errs.length) toast.warning(t("bkp_done_with_errors"));
      else toast.success(t("bkp_done"));
      load();
    } catch (e) {
      if (e?.response?.data?.detail === "NO_PASSWORD") { setPwModal(true); toast.error(t("bkp_need_password")); }
      else toast.error(e?.response?.data?.detail || t("bkp_failed"));
    } finally { setRunning(false); }
  };

  const download = (bid) => {
    // téléchargement direct (cookie httpOnly envoyé par le navigateur)
    window.location.href = `${API}/backup/${bid}/download`;
  };

  const del = async (bid) => {
    if (!window.confirm(t("bkp_delete_confirm"))) return;
    try { await api.delete(`/backup/${bid}`); toast.success(t("delete")); load(); }
    catch { toast.error(t("bkp_failed")); }
  };

  const restore = async (bid) => {
    if (!window.confirm(t("bkp_restore_confirm1"))) return;
    if (!window.confirm(t("bkp_restore_confirm2"))) return;
    const tid = toast.loading(t("bkp_restoring"));
    try {
      const { data } = await api.post(`/backup/${bid}/restore`, {}, { timeout: 300000 });
      toast.success(`${t("bkp_restored")} — ${data.collections} ${t("bkp_collections")}, ${data.files} ${t("bkp_files")}`, { id: tid });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("bkp_failed"), { id: tid }); }
  };

  const toggleSchedule = async (patch) => {
    const s = { enabled: cfg.schedule.enabled, hour: cfg.schedule.hour, retention: cfg.retention, ...patch };
    try { const { data } = await api.put("/backup/schedule", s); setCfg(data); }
    catch { toast.error(t("bkp_failed")); }
  };

  const testDest = async (did) => {
    const tid = toast.loading(t("bkp_testing"));
    try { await api.post(`/backup/destinations/${did}/test`); toast.success(t("bkp_test_ok"), { id: tid }); }
    catch (e) { toast.error(e?.response?.data?.detail || t("bkp_test_fail"), { id: tid }); }
  };

  const delDest = async (did) => {
    if (!window.confirm(t("bkp_dest_delete_confirm"))) return;
    try { const { data } = await api.delete(`/backup/destinations/${did}`); setCfg(data); toast.success(t("delete")); }
    catch { toast.error(t("bkp_failed")); }
  };

  if (!cfg) return <div className="py-20 text-center text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="space-y-5" data-testid="backup-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0" data-testid="backup-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold leading-tight flex items-center gap-2"><Icons.DatabaseBackup className="h-6 w-6 text-brand dark:text-primary" /> {t("bkp_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("bkp_sub")}</p>
        </div>
      </div>

      {/* Chiffrement obligatoire */}
      {!cfg.encryption_set && (
        <div className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-4 flex items-start gap-3" data-testid="bkp-no-password">
          <Icons.ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-800 dark:text-amber-300">{t("bkp_need_password")}</p>
            <p className="text-xs text-amber-700/80 dark:text-amber-300/70 mt-0.5">{t("bkp_password_hint")}</p>
            <button onClick={() => setPwModal(true)} data-testid="bkp-set-password-btn"
              className="mt-3 h-10 px-4 rounded-xl bg-amber-600 text-white font-bold text-sm">{t("bkp_set_password")}</button>
          </div>
        </div>
      )}

      {/* Action principale */}
      <div className="rounded-3xl bg-gradient-to-br from-brand to-brand-dark text-white p-6 shadow-ios" data-testid="bkp-hero">
        <p className="text-white/70 text-sm font-semibold uppercase tracking-wider">{t("bkp_last")}</p>
        <p className="text-lg font-bold mt-1">{backups[0] ? fmtDate(backups[0].created_at) : t("bkp_never")}</p>
        <button onClick={runBackup} disabled={running} data-testid="bkp-run-btn"
          className="mt-4 w-full h-14 rounded-full bg-white text-brand font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-70">
          {running ? <><span className="h-5 w-5 rounded-full border-2 border-brand border-t-transparent animate-spin" /> {t("bkp_running")}</>
                   : <><Icons.CloudUpload className="h-5 w-5" /> {t("bkp_run_now")}</>}
        </button>
      </div>

      {/* Planification quotidienne */}
      <div className="rounded-2xl bg-card border border-border p-4 shadow-ios space-y-3" data-testid="bkp-schedule">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Icons.CalendarClock className="h-5 w-5 text-brand dark:text-primary shrink-0" />
            <span className="font-bold">{t("bkp_daily")}</span>
          </div>
          <button onClick={() => toggleSchedule({ enabled: !cfg.schedule.enabled })} data-testid="bkp-schedule-toggle"
            className={`h-8 w-14 rounded-full p-1 transition-colors ${cfg.schedule.enabled ? "bg-brand" : "bg-secondary"}`}>
            <span className={`block h-6 w-6 rounded-full bg-white transition-transform ${cfg.schedule.enabled ? "translate-x-6 rtl:-translate-x-6" : ""}`} />
          </button>
        </div>
        {cfg.schedule.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-muted-foreground font-semibold">{t("bkp_hour")}</span>
              <select value={cfg.schedule.hour} onChange={(e) => toggleSchedule({ hour: Number(e.target.value) })} data-testid="bkp-hour"
                className="w-full h-11 rounded-xl border border-input bg-background px-3 mt-1 outline-none">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground font-semibold">{t("bkp_retention")}</span>
              <input type="number" min="1" max="365" value={cfg.retention} onChange={(e) => toggleSchedule({ retention: Number(e.target.value) })} data-testid="bkp-retention"
                className="w-full h-11 rounded-xl border border-input bg-background px-3 mt-1 outline-none" />
            </label>
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t("bkp_schedule_hint")}</p>
      </div>

      {/* Destinations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg flex items-center gap-2"><Icons.HardDriveUpload className="h-5 w-5 text-brand dark:text-primary" /> {t("bkp_destinations")}</h2>
        </div>
        <p className="text-xs text-muted-foreground -mt-1">{t("bkp_dest_hint")}</p>
        {cfg.destinations.map((d) => {
          const meta = DEST_TYPES.find((x) => x.type === d.type) || DEST_TYPES[0];
          const Ico = Icons[meta.icon] || Icons.Cloud;
          return (
            <div key={d.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`bkp-dest-${d.id}`}>
              <div className="flex items-start gap-3">
                <span className="h-10 w-10 rounded-xl bg-brand/10 text-brand dark:text-primary flex items-center justify-center shrink-0"><Ico className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="font-bold flex items-center gap-2">{d.label} <span className="text-[10px] font-bold bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full uppercase">{d.type}</span></p>
                  <p className="text-xs text-muted-foreground truncate">{destSummary(d)}</p>
                </div>
                {!d.enabled && <span className="text-[10px] font-bold bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{t("bkp_disabled")}</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => testDest(d.id)} data-testid={`bkp-dest-test-${d.id}`} className="flex-1 h-9 rounded-lg bg-secondary text-secondary-foreground text-sm font-semibold flex items-center justify-center gap-1.5"><Icons.Plug className="h-4 w-4" /> {t("bkp_test")}</button>
                <button onClick={() => setDestModal(d)} data-testid={`bkp-dest-edit-${d.id}`} className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center"><Icons.Pencil className="h-4 w-4" /></button>
                <button onClick={() => delDest(d.id)} data-testid={`bkp-dest-delete-${d.id}`} className="h-9 w-9 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center"><Icons.Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
        <div className="grid grid-cols-3 gap-2">
          {DEST_TYPES.map((x) => {
            const Ico = Icons[x.icon] || Icons.Cloud;
            return (
              <button key={x.type} onClick={() => setDestModal({ type: x.type })} data-testid={`bkp-add-${x.type}`}
                className="h-20 rounded-2xl border border-dashed border-input bg-background flex flex-col items-center justify-center gap-1 text-sm font-semibold px-1 text-center active:scale-95 transition-transform">
                <Ico className="h-5 w-5 text-brand dark:text-primary" /> <span className="text-xs">{x.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Historique */}
      <div className="space-y-3">
        <h2 className="font-bold text-lg flex items-center gap-2"><Icons.History className="h-5 w-5 text-brand dark:text-primary" /> {t("bkp_history")}</h2>
        {backups.length === 0 ? (
          <div className="rounded-2xl bg-card border border-border p-8 text-center text-muted-foreground" data-testid="bkp-empty">
            <Icons.Archive className="h-9 w-9 mx-auto mb-2 opacity-40" /> {t("bkp_none")}
          </div>
        ) : backups.map((b) => (
          <div key={b.id} className="rounded-2xl bg-card border border-border p-4" data-testid={`bkp-item-${b.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold flex items-center gap-2">
                  {fmtDate(b.created_at)}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${b.kind === "auto" ? "bg-majorelle/15 text-majorelle" : b.kind === "pre_restore" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" : "bg-brand/10 text-brand dark:text-primary"}`}>
                    {t("bkp_kind_" + b.kind)}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fmtSize(b.size)} · {b.files_count} {t("bkp_files")} · {totalRecords(b)} {t("bkp_records")}
                </p>
                {(b.destinations || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`bkp-item-dests-${b.id}`}>
                    {b.destinations.map((r, i) => (
                      <span key={`${r.type}-${r.label || ""}-${i}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1 ${r.status === "ok" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
                        {r.status === "ok" ? <Icons.Check className="h-3 w-3" /> : <Icons.X className="h-3 w-3" />} {r.label || r.type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => download(b.id)} data-testid={`bkp-download-${b.id}`} className="flex-1 h-9 rounded-lg bg-brand text-white text-sm font-semibold flex items-center justify-center gap-1.5"><Icons.Download className="h-4 w-4" /> {t("download")}</button>
              <button onClick={() => restore(b.id)} data-testid={`bkp-restore-${b.id}`} className="flex-1 h-9 rounded-lg bg-terracotta text-white text-sm font-semibold flex items-center justify-center gap-1.5"><Icons.RotateCcw className="h-4 w-4" /> {t("bkp_restore")}</button>
              <button onClick={() => del(b.id)} data-testid={`bkp-delete-${b.id}`} className="h-9 w-9 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center"><Icons.Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Restaurer depuis un fichier */}
      <button onClick={() => setRestoreFile(true)} data-testid="bkp-restore-upload-btn"
        className="w-full h-12 rounded-xl border border-input font-semibold text-sm flex items-center justify-center gap-2">
        <Icons.Upload className="h-4 w-4" /> {t("bkp_restore_from_file")}
      </button>

      {/* Zone de danger — remise à zéro production (président) */}
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-4 space-y-3 mt-4" data-testid="wipe-zone">
        <h2 className="font-bold text-lg flex items-center gap-2 text-destructive">
          <Icons.TriangleAlert className="h-5 w-5" /> {t("wipe_zone_title")}
        </h2>
        <p className="text-sm text-muted-foreground">{t("wipe_desc")}</p>
        <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
          <Icons.Info className="h-4 w-4 shrink-0 mt-0.5" /> {t("wipe_backup_first")}
        </p>
        <button onClick={() => setWipeModal(true)} data-testid="wipe-open-btn"
          className="w-full h-12 rounded-xl bg-destructive text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Icons.Trash2 className="h-4 w-4" /> {t("wipe_button")}
        </button>
      </div>

      {pwModal && <PasswordModal t={t} onClose={() => setPwModal(false)} onSaved={() => { setPwModal(false); load(); }} />}
      {destModal && <DestModal t={t} initial={destModal} onClose={() => setDestModal(null)} onSaved={(c) => { setCfg(c); setDestModal(null); }} />}
      {restoreFile && <RestoreUploadModal t={t} onClose={() => setRestoreFile(false)} onDone={() => { setRestoreFile(false); load(); }} />}
      {wipeModal && <WipeModal t={t} keptId={user?.identifier} onClose={() => setWipeModal(false)} onDone={() => { setWipeModal(false); load(); }} />}
    </div>
  );
}

function destSummary(d) {
  const s = d.settings || {};
  if (d.type === "s3") return `${s.bucket || "?"}${s.prefix ? "/" + s.prefix : ""} · ${s.storage_class || "STANDARD"}`;
  if (d.type === "azure") return `${s.account || "?"} / ${s.container || "?"}`;
  if (d.type === "sftp") return `${s.username || "?"}@${s.host || "?"}:${s.port || 22}${s.remote_dir || ""}`;
  return "";
}

function PasswordModal({ t, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pw.length < 8) return toast.error(t("bkp_password_min"));
    if (pw !== pw2) return toast.error(t("bkp_password_mismatch"));
    setBusy(true);
    try { await api.post("/backup/encryption", { password: pw }); toast.success(t("saved")); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("bkp_failed")); } finally { setBusy(false); }
  };
  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <Sheet title={t("bkp_set_password")} onClose={onClose} testid="bkp-password-modal">
      <p className="text-sm text-muted-foreground">{t("bkp_password_warn")}</p>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("bkp_password_ph")} data-testid="bkp-pw1" className={field} />
      <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder={t("bkp_password_confirm")} data-testid="bkp-pw2" className={field} />
      <button onClick={save} disabled={busy} data-testid="bkp-pw-save" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("save")}</button>
    </Sheet>
  );
}

function DestModal({ t, initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const type = initial.type;
  const s = initial.settings || {};
  const [form, setForm] = useState({
    label: initial.label || "", enabled: initial.enabled ?? true,
    endpoint: s.endpoint || "", region: s.region || "us-east-1", bucket: s.bucket || "", prefix: s.prefix || "", storage_class: s.storage_class || "STANDARD", access_key_id: s.access_key_id || "", secret_access_key: "",
    account: s.account || "", container: s.container || "", account_key: "",
    host: s.host || "", port: s.port || 22, username: s.username || "", remote_dir: s.remote_dir || "/", password: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { type, ...form };
      const { data } = isEdit ? await api.put(`/backup/destinations/${initial.id}`, body) : await api.post("/backup/destinations", body);
      toast.success(t("saved")); onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("bkp_failed")); } finally { setBusy(false); }
  };
  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  const title = { s3: "Amazon S3 / Glacier", azure: "Azure Blob", sftp: "Synology / SFTP" }[type];
  const secretPh = isEdit ? t("bkp_secret_keep") : "";
  return (
    <Sheet title={title} onClose={onClose} testid="bkp-dest-modal">
      <input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder={t("bkp_dest_label")} data-testid="bkp-dest-label" className={field} />
      {type === "s3" && (<>
        <input value={form.bucket} onChange={(e) => set("bucket", e.target.value)} placeholder="Bucket" data-testid="bkp-s3-bucket" className={field} />
        <input value={form.endpoint} onChange={(e) => set("endpoint", e.target.value)} placeholder={t("bkp_s3_endpoint")} data-testid="bkp-s3-endpoint" className={field} />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="Region" data-testid="bkp-s3-region" className={field} />
          <select value={form.storage_class} onChange={(e) => set("storage_class", e.target.value)} data-testid="bkp-s3-class" className={field}>
            <option value="STANDARD">Standard</option>
            <option value="STANDARD_IA">Standard-IA</option>
            <option value="GLACIER">Glacier</option>
            <option value="DEEP_ARCHIVE">Glacier Deep Archive</option>
          </select>
        </div>
        <input value={form.prefix} onChange={(e) => set("prefix", e.target.value)} placeholder={t("bkp_folder")} data-testid="bkp-s3-prefix" className={field} />
        <input value={form.access_key_id} onChange={(e) => set("access_key_id", e.target.value)} placeholder="Access Key ID" data-testid="bkp-s3-key" className={`${field} font-mono`} />
        <input type="password" value={form.secret_access_key} onChange={(e) => set("secret_access_key", e.target.value)} placeholder={`Secret Access Key ${secretPh}`} data-testid="bkp-s3-secret" className={`${field} font-mono`} />
      </>)}
      {type === "azure" && (<>
        <input value={form.account} onChange={(e) => set("account", e.target.value)} placeholder={t("bkp_az_account")} data-testid="bkp-az-account" className={field} />
        <input value={form.container} onChange={(e) => set("container", e.target.value)} placeholder="Container" data-testid="bkp-az-container" className={field} />
        <input value={form.prefix} onChange={(e) => set("prefix", e.target.value)} placeholder={t("bkp_folder")} data-testid="bkp-az-prefix" className={field} />
        <input type="password" value={form.account_key} onChange={(e) => set("account_key", e.target.value)} placeholder={`${t("bkp_az_key")} ${secretPh}`} data-testid="bkp-az-key" className={`${field} font-mono`} />
      </>)}
      {type === "sftp" && (<>
        <div className="grid grid-cols-3 gap-2">
          <input value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="Hôte / IP" data-testid="bkp-sftp-host" className={`${field} col-span-2`} />
          <input type="number" value={form.port} onChange={(e) => set("port", Number(e.target.value))} placeholder="Port" data-testid="bkp-sftp-port" className={field} />
        </div>
        <input value={form.username} onChange={(e) => set("username", e.target.value)} placeholder={t("bkp_sftp_user")} data-testid="bkp-sftp-user" className={field} />
        <input value={form.remote_dir} onChange={(e) => set("remote_dir", e.target.value)} placeholder={t("bkp_sftp_dir")} data-testid="bkp-sftp-dir" className={`${field} font-mono`} />
        <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={`${t("bkp_sftp_pw")} ${secretPh}`} data-testid="bkp-sftp-pw" className={field} />
      </>)}
      <button onClick={save} disabled={busy} data-testid="bkp-dest-save" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("save")}</button>
    </Sheet>
  );
}

function WipeModal({ t, keptId, onClose, onDone }) {
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = word.trim().toUpperCase() === "SUPPRIMER";
  const submit = async () => {
    if (!ok) return toast.error(t("wipe_confirm_mismatch"));
    setBusy(true);
    const tid = toast.loading(t("wipe_running"));
    try {
      const { data } = await api.post("/data/wipe", { confirm: word.trim().toUpperCase() }, { timeout: 120000 });
      toast.success(`${t("wipe_done")} — ${data.total} ${t("wipe_items")}`, { id: tid, duration: 6000 });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t("bkp_failed"), { id: tid }); }
    finally { setBusy(false); }
  };
  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <Sheet title={t("wipe_modal_title")} onClose={onClose} testid="wipe-modal">
      <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-3 flex items-start gap-2">
        <Icons.TriangleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <p className="text-sm text-destructive font-medium">{t("wipe_warn")}</p>
      </div>
      {keptId && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Icons.UserCheck className="h-4 w-4 text-emerald-600 shrink-0" />
          {t("wipe_kept")} : <span className="font-mono font-bold">{keptId}</span>
        </p>
      )}
      <label className="block text-sm font-semibold mt-1">{t("wipe_confirm_label")}</label>
      <input value={word} onChange={(e) => setWord(e.target.value)} placeholder={t("wipe_confirm_ph")}
        data-testid="wipe-confirm-input" autoFocus className={`${field} font-mono tracking-wider`} />
      <button onClick={submit} disabled={busy || !ok} data-testid="wipe-confirm-btn"
        className="w-full h-12 rounded-xl bg-destructive text-white font-bold disabled:opacity-40 flex items-center justify-center gap-2">
        {busy ? t("wipe_running") : <><Icons.Trash2 className="h-4 w-4" /> {t("wipe_button")}</>}
      </button>
    </Sheet>
  );
}


function RestoreUploadModal({ t, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error(t("bkp_choose_file"));
    if (!pw) return toast.error(t("bkp_password_ph"));
    if (!window.confirm(t("bkp_restore_confirm2"))) return;
    setBusy(true);
    const tid = toast.loading(t("bkp_restoring"));
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("password", pw);
      const { data } = await api.post("/backup/restore-upload", fd, { timeout: 300000, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("bkp_restored")} — ${data.collections} ${t("bkp_collections")}`, { id: tid });
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "BAD_PASSWORD" ? t("bkp_bad_password") : (d || t("bkp_failed")), { id: tid });
    } finally { setBusy(false); }
  };
  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <Sheet title={t("bkp_restore_from_file")} onClose={onClose} testid="bkp-restore-upload-modal">
      <p className="text-sm text-muted-foreground">{t("bkp_restore_file_hint")}</p>
      <input ref={fileRef} type="file" accept=".zip" data-testid="bkp-restore-file" className={field + " pt-3"} />
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("bkp_password_ph")} data-testid="bkp-restore-pw" className={field} />
      <button onClick={submit} disabled={busy} data-testid="bkp-restore-upload-submit" className="w-full h-12 rounded-xl bg-terracotta text-white font-bold disabled:opacity-60">{busy ? t("bkp_restoring") : t("bkp_restore")}</button>
    </Sheet>
  );
}

function Sheet({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-card text-foreground rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
